import { useTranslation } from "@/i18n";
import { FileUp, FolderInput, Loader, SquareCheckBig } from "lucide-react";

/**
 * Workflow step identifier.
 */
type WorkflowStep =
  | "selection"
  | "analyzing"
  | "review"
  | "processing"
  | "completed"
  | "error";

/**
 * Props for the ProcessSidebar component.
 */
interface ProcessSidebarProps {
  /** Whether a re-run operation is in progress */
  rerunning: boolean;
  /** The current active step in the workflow */
  currentStep?: WorkflowStep;
}

/**
 * Process sidebar component that displays workflow progress.
 * Shows a vertical timeline of steps with visual indicators for completion status.
 *
 * Each step is highlighted when active, and completed steps are marked with purple styling.
 *
 * @param props - Component props
 * @returns Sidebar with step progression indicators
 */
export default function ProcessSidebar({
  rerunning,
  currentStep = "selection",
}: ProcessSidebarProps) {
  const { t } = useTranslation();

  const steps = [
    {
      key: "upload",
      title: t("documentUpload.sidebar.selectDocument.title"),
      description: t("documentUpload.sidebar.selectDocument.description"),
      icon: FileUp,
    },
    {
      key: "analyse",
      title: t("documentUpload.sidebar.analyze.title"),
      description: t("documentUpload.sidebar.analyze.description"),
      icon: Loader,
    },
    {
      key: "review",
      title: t("documentUpload.sidebar.review.title"),
      description: t("documentUpload.sidebar.review.description"),
      icon: SquareCheckBig,
    },
    {
      key: "convert",
      title: t("documentUpload.sidebar.export.title"),
      description: t("documentUpload.sidebar.export.description"),
      icon: FolderInput,
    },
  ] as const;

  /**
   * Maps workflow step names to their numeric indices.
   */
  const stepMapping: Record<WorkflowStep, number> = {
    selection: 0,
    analyzing: 1,
    review: 2,
    processing: 3,
    completed: 4,
    error: 3, // Error occurs during processing, so show processing step
  };

  const activeIndex = stepMapping[currentStep];

  /**
   * Determines the completion status of a step based on current progress.
   *
   * @param idx - The step index to check
   * @returns "completed" if the step is done or active, "pending" otherwise
   */
  const getStepStatus = (idx: number) => {
    if (currentStep === "completed") {
      return "completed"; // All steps completed
    }

    // Each step is "completed" (purple) when we are currently in that step or have passed it
    if (idx <= activeIndex) return "completed";
    return "pending";
  };

  /**
   * Returns status text for specific steps during processing.
   * Currently unused but reserved for future loading indicators.
   *
   * @param idx - The step index
   * @returns Empty string for active processing steps, null otherwise
   */
  const getStatusIndicator = (idx: number) => {
    if (idx === 1 && currentStep === "analyzing") return "";
    if (idx === 2 && rerunning) return "";
    if (idx === 3 && currentStep === "processing") return "";
    return null;
  };

  return (
    <nav className="w-[158px]">
      <div className="space-y-0">
        {steps.map((step, idx) => {
          const status = getStepStatus(idx);
          const statusText = getStatusIndicator(idx);
          const isCompleted = status === "completed";

          return (
            <div key={step.key} className="flex items-start gap-2.5">
              {/* Left border indicator */}
              <div className="flex flex-col items-center">
                <div
                  className={`w-1 ${idx === 0 ? "rounded-t-full" : ""} ${
                    isCompleted ? "bg-[#8e2bf0]" : "bg-[#f4f4f5]"
                  }`}
                  style={{ height: "198px" }}
                />
              </div>

              {/* Content */}
              <div className="flex flex-col gap-2 pb-6">
                {/* Icon container */}
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    isCompleted ? "bg-[#8e2bf0]" : "bg-[#f4f4f5]"
                  }`}
                >
                  <step.icon
                    size={16}
                    className={`${
                      isCompleted ? "text-white" : "text-[#71717a]"
                    }`}
                  />
                </div>

                {/* Text content */}
                <div className="w-36">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-[#3f3f46] leading-[1.32]">
                      {step.title}
                    </h3>
                    {statusText && (
                      <span className="text-xs text-[#a1a1aa] ml-1">
                        {statusText}
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-[#a1a1aa] leading-[1.32] h-[26px] overflow-hidden">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
